// ======================================================================
// FILE: src/modules/wms/checklist/components/ChecklistItemOptionsTab.js
// MODULE: Work Management System (WMS)
// FEATURE: Checklist Item Options Management
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import wmsChecklistService
from "../../services/wmsChecklistService";

const DEFAULT_FORM = {

  sequence_no: "",

  option_value: "",

  option_label: "",

  score_value: "",

  is_default: false,

  remarks: "",
};

export default function ChecklistItemOptionsTab({
  itemId,
  itemTitle,
}) {

  const [loading, setLoading] =
    useState(false);

  const [rows, setRows] =
    useState([]);

  const [showModal, setShowModal] =
    useState(false);

  const [editingOption, setEditingOption] =
    useState(null);

  const [form, setForm] =
    useState(DEFAULT_FORM);

  useEffect(() => {

    if (itemId) {

      loadOptions();
    }

  }, [itemId]);

  async function loadOptions() {

    try {

      setLoading(true);

      const data =
        await wmsChecklistService
          .getOptions(itemId);

      setRows(data || []);

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Failed to load options"
      );

    } finally {

      setLoading(false);
    }
  }

  function handleAdd() {

    setEditingOption(null);

    setForm(DEFAULT_FORM);

    setShowModal(true);
  }

  function handleEdit(row) {

    setEditingOption(row);

    setForm({

      sequence_no:
        row.sequence_no || "",

      option_value:
        row.option_value || "",

      option_label:
        row.option_label || "",

      score_value:
        row.score_value ?? "",

      is_default:
        row.is_default || false,

      remarks:
        row.remarks || "",
    });

    setShowModal(true);
  }

  async function handleSave() {

    if (!form.sequence_no) {

      alert(
        "Sequence is required"
      );

      return;
    }

    if (!form.option_value) {

      alert(
        "Option Value is required"
      );

      return;
    }

    if (!form.option_label) {

      alert(
        "Option Label is required"
      );

      return;
    }

    try {

      const payload = {

        ...form,

        score_value:
          form.score_value === ""
            ? null
            : Number(
                form.score_value
              ),
      };

      if (editingOption) {

        await wmsChecklistService
          .updateOption(
            itemId,
            editingOption.id,
            payload
          );

      } else {

        await wmsChecklistService
          .createOption(
            itemId,
            payload
          );
      }

      setShowModal(false);

      await loadOptions();

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Save failed"
      );
    }
  }

  async function handleDelete(row) {

    const ok = window.confirm(
      `Delete option "${row.option_label}" ?`
    );

    if (!ok) {
      return;
    }

    try {

      await wmsChecklistService
        .deleteOption(
          itemId,
          row.id
        );

      await loadOptions();

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Delete failed"
      );
    }
  }

  return (

    <div className="card mt-3">

      <div
        className="
          card-header
          d-flex
          justify-content-between
          align-items-center
        "
      >

        <strong>

          Item Options
          {" "}
          -
          {" "}
          {itemTitle}

        </strong>

        <button
          className="btn btn-sm btn-primary"
          onClick={handleAdd}
        >
          Add Option
        </button>

      </div>

      <div className="card-body p-0">

        <table
          className="
            table
            table-striped
            table-hover
            mb-0
          "
        >

          <thead>

            <tr>

              <th>Seq</th>

              <th>Value</th>

              <th>Label</th>

              <th>Score</th>

              <th>Default</th>

              <th width="180">
                Actions
              </th>

            </tr>

          </thead>

          <tbody>

            {loading && (

              <tr>

                <td
                  colSpan="6"
                  className="text-center"
                >
                  Loading...
                </td>

              </tr>

            )}

            {!loading &&
              rows.length === 0 && (

              <tr>

                <td
                  colSpan="6"
                  className="text-center"
                >
                  No Options Found
                </td>

              </tr>

            )}

            {!loading &&
              rows.map((row) => (

              <tr key={row.id}>

                <td>
                  {row.sequence_no}
                </td>

                <td>
                  {row.option_value}
                </td>

                <td>
                  {row.option_label}
                </td>

                <td>
                  {row.score_value}
                </td>

                <td>
                  {row.is_default
                    ? "Yes"
                    : "No"}
                </td>

                <td>

                  <button
                    className="
                      btn
                      btn-sm
                      btn-primary
                      me-1
                    "
                    onClick={() =>
                      handleEdit(row)
                    }
                  >
                    Edit
                  </button>

                  <button
                    className="
                      btn
                      btn-sm
                      btn-danger
                    "
                    onClick={() =>
                      handleDelete(row)
                    }
                  >
                    Delete
                  </button>

                </td>

              </tr>

            ))}

          </tbody>

        </table>

      </div>

      {showModal && (

        <div
          className="modal d-block"
        >

          <div className="modal-dialog">

            <div className="modal-content">

              <div className="modal-header">

                <h5>

                  {editingOption
                    ? "Edit Option"
                    : "Add Option"}

                </h5>

              </div>

              <div className="modal-body">

                <div className="mb-3">

                  <label>
                    Sequence *
                  </label>

                  <input
                    type="number"
                    className="form-control"
                    value={
                      form.sequence_no
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        sequence_no:
                          e.target.value,
                      })
                    }
                  />

                </div>

                <div className="mb-3">

                  <label>
                    Option Value *
                  </label>

                  <input
                    className="form-control"
                    value={
                      form.option_value
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        option_value:
                          e.target.value,
                      })
                    }
                  />

                </div>

                <div className="mb-3">

                  <label>
                    Option Label *
                  </label>

                  <input
                    className="form-control"
                    value={
                      form.option_label
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        option_label:
                          e.target.value,
                      })
                    }
                  />

                </div>

                <div className="mb-3">

                  <label>
                    Score
                  </label>

                  <input
                    type="number"
                    className="form-control"
                    value={
                      form.score_value
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        score_value:
                          e.target.value,
                      })
                    }
                  />

                </div>

                <div className="form-check mb-3">

                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={
                      form.is_default
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        is_default:
                          e.target.checked,
                      })
                    }
                  />

                  <label
                    className="
                      form-check-label
                    "
                  >
                    Default Option
                  </label>

                </div>

                <div className="mb-3">

                  <label>
                    Remarks
                  </label>

                  <textarea
                    rows="3"
                    className="form-control"
                    value={
                      form.remarks
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        remarks:
                          e.target.value,
                      })
                    }
                  />

                </div>

              </div>

              <div className="modal-footer">

                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                >
                  Save
                </button>

                <button
                  className="btn btn-secondary"
                  onClick={() =>
                    setShowModal(false)
                  }
                >
                  Cancel
                </button>

              </div>

            </div>

          </div>

        </div>

      )}

    </div>
  );
}